import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api";

export function useConversations(projectId) {
  return useQuery({
    queryKey: ["conversations", projectId],
    queryFn: async () =>
      (await apiClient.get("/conversations", { params: { project_id: projectId } })).data,
    enabled: !!projectId,
  });
}

// «Chats eliminados» del proyecto: siguen guardados; se pueden ver, restaurar o eliminar definitivamente.
export function useDeletedConversations(projectId) {
  return useQuery({
    queryKey: ["conversations-deleted", projectId],
    queryFn: async () =>
      (await apiClient.get("/conversations", { params: { project_id: projectId, deleted: true } })).data,
    enabled: !!projectId,
  });
}

export function useMessages(conversationId, refetchInterval = false) {
  return useQuery({
    queryKey: ["messages", conversationId],
    queryFn: async () => (await apiClient.get(`/conversations/${conversationId}/messages`)).data,
    enabled: !!conversationId,
    refetchInterval, // mientras la IA responde: revisa cada pocos segundos si la respuesta ya quedó guardada
  });
}

// Cualquier cambio en los chats (principal, eliminar, restaurar) vuelve a leer la lista, la de eliminados y los planes del proyecto.
function useChatsMutation(projectId, mutationFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations", projectId] });
      queryClient.invalidateQueries({ queryKey: ["conversations-deleted", projectId] });
      queryClient.invalidateQueries({ queryKey: ["project-plans", projectId] });
    },
  });
}

// Deja un chat como el principal del proyecto.
export function useMakeMain(projectId) {
  return useChatsMutation(projectId, async (id) => (await apiClient.post(`/conversations/${id}/main`)).data);
}

// «Eliminar» un chat: pasa a «Chats eliminados». Si era el principal, `newMainId` dice cuál de los otros chats lo será.
export function useTrashConversation(projectId) {
  return useChatsMutation(projectId, async ({ id, newMainId }) => (await apiClient.post(`/conversations/${id}/trash`, { new_main_id: newMainId || null })).data);
}

export function useRestoreConversation(projectId) {
  return useChatsMutation(projectId, async (id) => (await apiClient.post(`/conversations/${id}/restore`)).data);
}

// Eliminar DEFINITIVAMENTE un chat que ya está en «Chats eliminados» (no se puede deshacer).
export function usePurgeConversation(projectId) {
  return useChatsMutation(projectId, async (id) => (await apiClient.delete(`/conversations/${id}`)).data);
}
